import {StyleSheet, css} from 'aphrodite';
import React, {useState, useMemo, useEffect} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import {autoMapDataFrameColumns} from './ColumnMappingUtil';
import {AutoMapperMode, AutoMapperColumnJoinType} from './ColumnMappingUtil';

export const AUTO_MAPPER_MODE_TO_DISPLAY: Record<AutoMapperMode, string> = {
  [AutoMapperMode.NAME]: 'By name',
  [AutoMapperMode.ORDER]: 'By order',
};

export const AUTO_MAPPER_COLUMN_JOIN_TYPE_TO_DISPLAY: Record<AutoMapperColumnJoinType, string> = {
  [AutoMapperColumnJoinType.KEEP_TEMPLATE]: 'Keep left columns',
  [AutoMapperColumnJoinType.KEEP_SHARED]: 'Keep shared columns',
  [AutoMapperColumnJoinType.KEEP_ALL]: 'Keep all columns',
};

export interface DataFrameColumnAutoMapperProps {
  templateColumns: Array<DataFrameColumnDef>;
  aligningColumns: Array<DataFrameColumnDef>;
  setSelectedTemplateColumns: (columns: Array<DataFrameColumnDef | null>) => void;
  setSelectedAligningColumns: (columns: Array<DataFrameColumnDef | null>) => void;
  initialAutoMapperMode: AutoMapperMode;
  initialAutoMapperColumnJoinType: AutoMapperColumnJoinType;
  allowModeChange: boolean;
  allowColumnJoinTypeChange: boolean;
  autoMap: boolean;
}

/**
 * This component that auto aligns columns given templateColumns and aligningColumns
 *  notifies the parent component of the auto mapped columns using setSelectedTemplateColumns and setSelectedAligningColumns
 *
 * Note: This component may result in endless render loops:
 *  templateColumns, aligningColumns, setSelectedTemplateColumns, setSelectedAligningColumns should be maintained as stable objects or memoized
 */
export default function DataFrameColumnAutoMapper({
  templateColumns,
  aligningColumns,
  setSelectedTemplateColumns,
  setSelectedAligningColumns,
  initialAutoMapperMode,
  initialAutoMapperColumnJoinType,
  allowModeChange,
  allowColumnJoinTypeChange,
  autoMap,
}: DataFrameColumnAutoMapperProps): JSX.Element {
  const [autoMapperMode, setAutoMapperMode] = useState<AutoMapperMode>(initialAutoMapperMode);
  const [autoMapperColumnJoinType, setAutoMapperColumnJoinType] = useState<AutoMapperColumnJoinType>(
    initialAutoMapperColumnJoinType
  );
  const [toTrigger, setToTrigger] = useState<boolean>(false);

  const {autoMappedTemplateColumns, autoMappedAligningColumns} = useMemo(
    () =>
      autoMapDataFrameColumns(templateColumns, aligningColumns, {
        mode: autoMapperMode,
        columnJoinType: autoMapperColumnJoinType,
      }),
    [templateColumns, aligningColumns, autoMapperMode, autoMapperColumnJoinType]
  );

  useEffect(() => {
    if (toTrigger) {
      setToTrigger(false);
      setSelectedTemplateColumns(autoMappedTemplateColumns);
      setSelectedAligningColumns(autoMappedAligningColumns);
    } else if (autoMap) {
      setSelectedTemplateColumns(autoMappedTemplateColumns);
      setSelectedAligningColumns(autoMappedAligningColumns);
    }
  }, [
    autoMappedTemplateColumns,
    autoMappedAligningColumns,
    setSelectedTemplateColumns,
    setSelectedAligningColumns,
    autoMap,
    toTrigger,
  ]);

  return (
    <div className={css(styles.controlContainer)}>
      {allowModeChange && (
        <ButtonGroup>
          <Button
            text={AUTO_MAPPER_MODE_TO_DISPLAY[autoMapperMode]}
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.SMALL}
            onClick={() => {
              setSelectedTemplateColumns(autoMappedTemplateColumns);
              setSelectedAligningColumns(autoMappedAligningColumns);
            }}
            dataTestAttrs={{element: 'button', key: 'auto_map_columns_mode'}}
          />
          <Dropdown
            menu={
              <Menu height={MenuHeight.MEDIUM} width={MenuWidth.SMALL} value={autoMapperMode}>
                {[AutoMapperMode.NAME, AutoMapperMode.ORDER].map((mode) => (
                  <Option
                    key={mode}
                    value={mode}
                    onSelect={() => {
                      if (mode === autoMapperMode) {
                        setSelectedTemplateColumns(autoMappedTemplateColumns);
                        setSelectedAligningColumns(autoMappedAligningColumns);
                      } else {
                        // automap happens after the next render in the useEffect
                        setAutoMapperMode(mode);
                        setToTrigger(true);
                      }
                    }}
                  >
                    {AUTO_MAPPER_MODE_TO_DISPLAY[mode]}
                  </Option>
                ))}
              </Menu>
            }
          >
            <Button isDropdown={true} buttonStyle={ButtonStyle.TERTIARY_BLUE} size={ButtonSize.SMALL} />
          </Dropdown>
        </ButtonGroup>
      )}
      {allowColumnJoinTypeChange && (
        <ButtonGroup>
          <Button
            text={AUTO_MAPPER_COLUMN_JOIN_TYPE_TO_DISPLAY[autoMapperColumnJoinType]}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            onClick={() => {
              setSelectedTemplateColumns(autoMappedTemplateColumns);
              setSelectedAligningColumns(autoMappedAligningColumns);
            }}
            dataTestAttrs={{element: 'button', key: 'auto_map_columns_join_type'}}
          />
          <Dropdown
            menu={
              <Menu height={MenuHeight.MEDIUM} width={MenuWidth.SMALL} value={autoMapperColumnJoinType}>
                {[
                  AutoMapperColumnJoinType.KEEP_TEMPLATE,
                  AutoMapperColumnJoinType.KEEP_SHARED,
                  AutoMapperColumnJoinType.KEEP_ALL,
                ].map((columnJoinType) => (
                  <Option
                    key={columnJoinType}
                    value={columnJoinType}
                    onSelect={() => {
                      if (columnJoinType === autoMapperColumnJoinType) {
                        setSelectedTemplateColumns(autoMappedTemplateColumns);
                        setSelectedAligningColumns(autoMappedAligningColumns);
                      } else {
                        setAutoMapperColumnJoinType(columnJoinType);
                        setToTrigger(true);
                      }
                    }}
                  >
                    {AUTO_MAPPER_COLUMN_JOIN_TYPE_TO_DISPLAY[columnJoinType]}
                  </Option>
                ))}
              </Menu>
            }
          >
            <Button isDropdown={true} buttonStyle={ButtonStyle.TERTIARY_BLUE} size={ButtonSize.SMALL} />
          </Dropdown>
        </ButtonGroup>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  controlContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
  },
});
